import { useEffect, useRef, useState } from "react";

interface DemoLine {
  text: string;
  tone?: "muted" | "dim";
  progress?: boolean;
}

interface Demo {
  id: string;
  label: string;
  command: string;
  lines: DemoLine[];
  link?: string;
}

const TYPE_MS = 26;
const LINE_MS = 340;
const HOLD_MS = 3200;

/**
 * The CLI, performed rather than displayed: the terminal types each command
 * live, streams its output, lands the link, holds, then moves to the next
 * session on its own. Clicking a tab jumps the loop; reduced-motion viewers
 * get the finished session with no theatrics.
 */
export function CliShowcase() {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState(0);
  const [shown, setShown] = useState(0);
  const [linkOn, setLinkOn] = useState(false);
  const demo = DEMOS[index % DEMOS.length]!;

  useEffect(() => {
    if (reduced) {
      setTyped(demo.command.length);
      setShown(demo.lines.length);
      setLinkOn(true);
      return;
    }

    setTyped(0);
    setShown(0);
    setLinkOn(false);
    let cancelled = false;
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => {
      timers.push(window.setTimeout(() => { if (!cancelled) fn(); }, ms));
    };

    for (let i = 1; i <= demo.command.length; i += 1) {
      later(() => setTyped(i), 260 + i * TYPE_MS);
    }
    const typedDone = 260 + demo.command.length * TYPE_MS + 240;
    for (let i = 1; i <= demo.lines.length; i += 1) {
      later(() => setShown(i), typedDone + i * LINE_MS);
    }
    const outputDone = typedDone + demo.lines.length * LINE_MS + 320;
    later(() => setLinkOn(true), outputDone);
    later(() => setIndex((current) => (current + 1) % DEMOS.length), outputDone + HOLD_MS);

    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [index, reduced, demo]);

  const typing = typed < demo.command.length;

  return (
    <div className="demo">
      <div className="demo__tabs" role="tablist" aria-label="CLI examples">
        {DEMOS.map((entry, i) => (
          <button
            key={entry.id}
            role="tab"
            aria-selected={i === index}
            className={`demo__tab${i === index ? " demo__tab--active" : ""}`}
            type="button"
            onClick={() => setIndex(i)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="terminal" role="tabpanel">
        <div className="terminal__bar" aria-hidden="true">
          <span className="terminal__dots">
            <i />
            <i />
            <i />
          </span>
          <span className="terminal__name mono">{demo.label} — zsh</span>
        </div>
        <pre className="terminal__body">
          <span className="terminal__line">
            <span className="terminal__prompt">$</span>
            {" "}
            {demo.command.slice(0, typed)}
            {typing || !linkOn ? <span className="terminal__cursor" aria-hidden="true" /> : null}
          </span>
          {demo.lines.slice(0, shown).map((line, i) => (
            <span
              key={i}
              className={`terminal__line terminal__line--enter${line.tone ? ` terminal__line--${line.tone}` : ""}`}
            >
              {line.progress ? (
                <>
                  {"  "}
                  <span className="terminal__progress" aria-hidden="true">
                    <i />
                  </span>
                  {" 100%"}
                </>
              ) : (
                line.text
              )}
            </span>
          ))}
        </pre>
        {demo.link && linkOn ? (
          <div className="terminal__link">
            <div className="linkbar">
              <span className="linkbar__url">{demo.link}</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function usePrefersReducedMotion(): boolean {
  const query = useRef(
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : null,
  );
  const [reduced, setReduced] = useState(query.current?.matches ?? false);

  useEffect(() => {
    const media = query.current;
    if (!media) return;
    const sync = () => setReduced(media.matches);
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return reduced;
}

const DEMOS: Demo[] = [
  {
    id: "send",
    label: "saucer send",
    command: "saucer send screen-recording.mp4",
    lines: [
      { text: "  screen-recording.mp4 · 148 MB · 5 parts", tone: "muted" },
      { text: "", progress: true, tone: "muted" },
    ],
    link: "https://saucer.sh/you/screen-recording.mp4",
  },
  {
    id: "server",
    label: "from a server",
    command: "saucer send /var/log/app/crash-$(date +%F).log",
    lines: [
      { text: "  crash-2026-08-21.log · 2.1 MB", tone: "muted" },
      { text: "", progress: true, tone: "muted" },
    ],
    link: "https://saucer.sh/you/crash-2026-08-21.log",
  },
  {
    id: "paste",
    label: "saucer paste",
    command: "saucer paste --password 203932",
    lines: [
      { text: "  clipboard image captured", tone: "dim" },
      { text: "  paste-20260820-114210.png · 891 KB", tone: "muted" },
      { text: "  locked — opens with the password", tone: "dim" },
    ],
    link: "https://saucer.sh/you/paste-20260820-114210.png",
  },
  {
    id: "agent",
    label: "from an agent",
    command: "SAUCER_TOKEN=sc_… saucer send report.pdf --quiet",
    lines: [],
    link: "https://saucer.sh/you/report.pdf",
  },
  {
    id: "search",
    label: "saucer search",
    command: "saucer search that chart about billing",
    lines: [
      { text: "  2 matches", tone: "dim" },
      { text: "  revenue-q3.png        billing dashboard, bar chart", tone: "muted" },
      { text: "  invoice-flow.pdf      payment sequence diagram", tone: "muted" },
    ],
  },
];
